import React, { useEffect, useState } from "react";
import { Mail, Send, MessageSquare, Clock, MapPin, Globe } from "lucide-react";
import { Helmet } from "react-helmet-async"; 
import "aos/dist/aos.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast, { Toaster } from 'react-hot-toast';
import { useSEO } from "../Pages/Seo";

const ContactUs = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    import("aos").then((AOS) => {
      AOS.default.init({ duration: 500, once: true });
    });
    window.scrollTo(0, 0);
  }, []);

  const validationSchema = Yup.object({
    firstName: Yup.string().required("First Name is required"),
    lastName: Yup.string().required("Last Name is required"),
    email: Yup.string().email("Invalid email address").required("Email is required"),
    subject: Yup.string().required("Subject is required"),
    message: Yup.string().min(10, "Message must be at least 10 characters").required("Message is required"),
  });

  useSEO({
    title: "Contact Us | Support for Free Online Games | Game Zone Hub",
    description: "Get in touch with the Game Zone Hub team. Need help with HTML5 browser games, want to report a bug, or have a business inquiry? We are here to assist you.",
    keywords: "contact game zone hub, customer support, online games support, free games help, report bug game zone",
    canonical: "https://www.gamezonehub.in/contact",
  });

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Game Zone Hub",
    "description": "Contact page for Game Zone Hub users to get support regarding free online games.",
    "url": "https://www.gamezonehub.in/contact",
    "mainEntity": {
      "@type": "Organization",
      "name": "Game Zone Hub",
      "url": "https://www.gamezonehub.in/",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer support",
        "email": "support@gamezonehub.in",
        "availableLanguage": "English"
      }
    }
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      subject: "General Inquiry",
      message: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values, { resetForm }) => {
      setIsSubmitting(true);

      setTimeout(() => {
        toast.success(`Message Sent Successfully! We will contact you soon.`, {
            duration: 4000,
            position: 'top-center',
            style: {
              background: '#1e293b', 
              color: '#fff',
              border: '1px solid #3b82f6',
              padding: '16px',
            },
            iconTheme: {
              primary: '#3b82f6',
              secondary: '#FFFAEE',
            },
        });
        
        setIsSubmitting(false);
        resetForm();
      }, 1000);
    },
  });

  return (
    <div className=" overflow-hidden bg-[#0f172a] sm:pt-[120px] pt-[100px] sm:pb-20 pb-11  text-white">
      
      <Toaster />

      <Helmet>
        {schemaData && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        )}
      </Helmet>

      <div className="container mx-auto px-4">
        <div className="text-center sm:mb-16 mb-11" data-aos="fade-down">
          <h1 className="text-3xl sm:text-5xl font-extrabold mb-4">
            Get in <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-300">Touch</span>
          </h1>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg leading-relaxed">
            Welcome to the official contact page of Game Zone Hub. Whether you have a question about our <strong>free online HTML5 games</strong>, want to share feedback, or need to report a technical bug, our dedicated support team is always ready to assist you.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          
          {/* 🚀 EXPANDED CONTENT SECTION (LEFT SIDE) */}
          <div className="lg:col-span-2 space-y-8" data-aos="fade-right">
            
            <div className="bg-[#1e293b] sm:p-8 p-6 rounded-3xl border border-white/5 shadow-lg">
              <h3 className="text-2xl font-bold mb-6 border-b border-white/10 pb-3">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-500/10 p-3 rounded-xl">
                    <Mail className="text-blue-400 w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg text-white">Email Support</h4>
                    <p className="text-gray-400 text-sm mb-1 leading-relaxed">Our primary method of communication. Use this email for general inquiries, player support, and technical issues:</p>
                    <a href="mailto:support@gamezonehub.in" className="text-blue-400 hover:text-blue-300 font-medium transition block mt-1">
                      support@gamezonehub.in
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-cyan-500/10 p-3 rounded-xl">
                    <Clock className="text-cyan-400 w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg text-white">Response Time & Hours</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      We aim to respond to all player inquiries within <strong>24 to 48 hours</strong>. Our technical team operates from Monday to Friday, 9:00 AM to 6:00 PM (IST).
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-purple-500/10 p-3 rounded-xl">
                    <Globe className="text-purple-400 w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg text-white">Business & Partnerships</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      Are you a game developer looking to publish your HTML5 game on our portal? Or an advertiser looking for partnership opportunities? Select "Advertising & Partnerships" in the form subject.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#1e293b] sm:p-8 p-6 rounded-3xl border border-white/5 shadow-lg">
               <h3 className="text-xl font-bold mb-4 border-b border-white/10 pb-3 flex items-center gap-2">
                 <MessageSquare className="text-blue-400" size={20}/> How Can We Help You?
               </h3>
               <div className="space-y-4 text-sm text-gray-400">
                   <div>
                     <strong className="text-white block mb-1">🐛 Bug Reporting:</strong>
                     <p className="leading-relaxed">If a specific game is not loading or the controls are unresponsive, please include the <strong>exact name of the game</strong> and your device/browser type in your message so we can fix it quickly.</p>
                   </div>
                   <div>
                     <strong className="text-white block mb-1">🎮 Game Requests:</strong>
                     <p className="leading-relaxed">Can't find your favorite classic browser game? Send us a request! We update our catalog regularly based on our community's suggestions.</p>
                   </div>
               </div>
            </div>

          </div>

          {/* RIGHT SIDE: CONTACT FORM */}
          <div className="lg:col-span-3 bg-[#1e293b] p-8 sm:p-10 rounded-3xl border border-white/5 shadow-lg h-fit" data-aos="fade-left">
            <h3 className="text-2xl font-bold mb-2">Send us a Message</h3>
            <p className="text-gray-400 mb-8 text-sm">Fill out the form below and we will get back to you as soon as possible.</p>
            
            <form className="space-y-6" onSubmit={formik.handleSubmit}>
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-300" htmlFor="firstName">First Name</label>
                  <input 
                    id="firstName"
                    type="text" 
                    name="firstName"
                    placeholder="John" 
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.firstName}
                    className={`w-full bg-[#0f172a] border rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-1 transition
                      ${formik.touched.firstName && formik.errors.firstName ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-white/10 focus:border-blue-500 focus:ring-blue-500"}
                    `}
                  />
                  {formik.touched.firstName && formik.errors.firstName && (
                    <div className="text-red-500 text-xs mt-1">{formik.errors.firstName}</div>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-300" htmlFor="lastName">Last Name</label>
                  <input 
                    id="lastName"
                    type="text" 
                    name="lastName"
                    placeholder="Doe" 
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.lastName}
                    className={`w-full bg-[#0f172a] border rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-1 transition
                      ${formik.touched.lastName && formik.errors.lastName ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-white/10 focus:border-blue-500 focus:ring-blue-500"}
                    `}
                  />
                  {formik.touched.lastName && formik.errors.lastName && (
                    <div className="text-red-500 text-xs mt-1">{formik.errors.lastName}</div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300" htmlFor="email">Email Address</label>
                <input 
                  id="email"
                  type="email" 
                  name="email"
                  placeholder="john@example.com" 
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  className={`w-full bg-[#0f172a] border rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-1 transition
                    ${formik.touched.email && formik.errors.email ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-white/10 focus:border-blue-500 focus:ring-blue-500"}
                  `}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className="text-red-500 text-xs mt-1">{formik.errors.email}</div>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300" htmlFor="subject">Subject</label>
                <select 
                  id="subject"
                  name="subject"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.subject}
                  className="w-full bg-[#0f172a] border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"
                >
                  <option value="General Inquiry">General Inquiry</option>
                  <option value="Report a Bug">Report a Bug / Technical Issue</option>
                  <option value="Advertising">Advertising & Partnerships</option>
                  <option value="Game Request">Game Submission / Request</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300" htmlFor="message">Message</label>
                <textarea 
                  id="message"
                  rows="5" 
                  name="message"
                  placeholder="Please describe your issue or inquiry in detail..." 
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.message}
                  className={`w-full bg-[#0f172a] border rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-1 transition resize-none
                    ${formik.touched.message && formik.errors.message ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-white/10 focus:border-blue-500 focus:ring-blue-500"}
                  `}
                ></textarea>
                {formik.touched.message && formik.errors.message && (
                  <div className="text-red-500 text-xs mt-1">{formik.errors.message}</div>
                )}
              </div>

              <button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-bold py-4 rounded-xl shadow-lg shadow-blue-500/20 transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed text-lg"
              >
                {isSubmitting ? (
                  <>Sending Message...</>
                ) : (
                  <> <Send size={20} /> Send Message </>
                )}
              </button>
            </form>
          </div>

        </div>
      </div>

    </div>
  );
};

export default ContactUs;